import { Path, Styles, Svg, View } from '@react-pdf/renderer';
import { Col, LabelValueText, PageContainer, Row, StyledText } from './components';
import { anyObjectProperty } from '../../../@types/anyObjectProperty';
import { dateMDY, formatAddress, getFormattedPhone } from '../../../lib/utils';

const LabelValueItem = ({
  label,
  value,
  valueWidth,
  style,
}: {
  label?: string;
  value?: string | number | null;
  valueWidth: string;
  style?: Styles[string];
}) => (
  <Row
    style={{
      marginBottom: '15pt',
      ...style,
    }}
  >
    <StyledText content={label} />
    <View
      style={{
        display: 'flex',
        width: valueWidth,
        borderBottom: '1px solid black',
      }}
    >
      {(!!value || value === 0) && <StyledText content={value} weight={600} />}
    </View>
  </Row>
);

export const ExternalRxLabel = ({ externalRxData }: { externalRxData: anyObjectProperty }) => {
  const { prescriber, patient } = externalRxData;
  return (
    <PageContainer>
      <View>
        <Row>
          <Col
            style={{
              width: '60%',
              flexWrap: 'wrap',
            }}
          >
            <LabelValueItem
              label='Date Written'
              value={externalRxData.prescriptionTransferDetails?.writtenDate && dateMDY(externalRxData.prescriptionTransferDetails.writtenDate)}
              valueWidth='248pt'
            />
            <LabelValueItem
              label='Doctor'
              value={prescriber && `${prescriber.firstName} ${prescriber.lastName}`}
              valueWidth='248pt'
            />
            <LabelValueItem label='Agent Name' valueWidth='248pt' />
            <Row
              style={{
                justifyContent: 'flex-start',
              }}
            >
              <StyledText
                content='Address'
                style={{
                  marginRight: '10pt',
                }}
              />
              <StyledText
                content={formatAddress(prescriber.address)}
                weight={600}
              />
            </Row>
          </Col>
          <Col
            style={{
              width: '35%',
              flexWrap: 'wrap',
            }}
          >
            <LabelValueItem label='Phone' value={getFormattedPhone(prescriber?.phoneNumber)} valueWidth='132pt' />
            <LabelValueItem label='SLN' valueWidth='132pt' />
            <LabelValueItem label='DEA' value={prescriber?.dea} valueWidth='132pt' />
            <LabelValueItem label='NPI' value={prescriber?.npi} valueWidth='132pt' />
          </Col>
        </Row>
        <View
          style={{
            width: '100%',
            height: '4pt',
            backgroundColor: 'black',
            marginBottom: '15pt',
          }}
        />
        <Row
          style={{
            marginBottom: '40pt',
          }}
        >
          <Col
            style={{
              width: '60%',
            }}
          >
            <LabelValueItem
              label='Name'
              value={patient && `${patient.firstName} ${patient.lastName}`}
              valueWidth='248pt'
            />
            <Row
              style={{
                justifyContent: 'flex-start',
                marginBottom: '15pt',
              }}
            >
              <StyledText
                content='Address'
                style={{
                  marginRight: '10pt',
                }}
              />
              <StyledText
                content={formatAddress(patient.address)}
                weight={600}
              />
            </Row>
            <Row
              style={{
                justifyContent: 'flex-start',
                marginBottom: '15pt',
              }}
            >
              <StyledText
                content='Email'
                style={{
                  marginRight: '25pt',
                }}
              />
              <StyledText
                content={patient?.email}
                weight={600}
                style={{
                  marginBottom: '15pt',
                }}
              />
            </Row>
            <View
              style={{
                display: 'flex',
                flexDirection: 'row',
                marginBottom: '40pt',
              }}
            >
              <Svg
                width='63'
                height='75'
                viewBox='0 0 63 75'
                fill='none'
                style={{
                  marginRight: '21pt',
                }}
              >
                <Path
                  d='M24.1909 0C23.4548 4.59432e-05 22.2688 0.0202857 20.6356 0.0662642C16.2192 0.227373 12.7128 0.309282 10.1136 0.309233C7.76734 0.309282 4.4854 0.217248 0.276025 0.0331321L0 0.276101V1.99897L0.276025 2.24194C2.71424 2.33402 4.24802 2.46747 4.86909 2.62848C5.49013 2.78958 5.90923 2.9939 6.12776 3.24694C6.34627 3.50008 6.51005 4.00074 6.61356 4.75998C6.71706 5.5193 6.77826 7.21731 6.80126 9.84023L6.86751 16.566V32.5468L6.83441 39.8248C6.81141 42.2867 6.71893 43.8825 6.56942 44.5958C6.4199 45.3091 6.22576 45.7688 5.98425 45.9873C5.74272 46.2059 5.27302 46.3904 4.55996 46.5285C3.84689 46.6665 2.41523 46.7788 0.276041 46.8709L1.61734e-05 47.0807V48.8036L0.276041 49.0797C6.14157 48.8956 9.43363 48.8036 10.1467 48.8036C11.0668 48.8036 12.3448 48.8238 13.9779 48.8698C17.9113 49.0079 19.9355 49.0797 20.0505 49.0797L20.3265 48.8036V47.0807L20.0505 46.8709C17.6123 46.7788 16.0785 46.6458 15.4574 46.4733C14.8364 46.3007 14.4173 46.0858 14.1988 45.8327C13.9802 45.5796 13.8169 45.079 13.7019 44.3197C13.5869 43.5604 13.5151 41.8725 13.4921 39.2726L13.459 32.5468V16.566C13.459 8.44412 13.6329 4.12867 13.978 3.62244C16.1171 2.95524 18.3861 2.62852 20.8013 2.62848C24.2516 2.62852 26.9916 3.40575 29.0158 4.95877C31.04 6.51188 32.0521 8.80213 32.0521 11.8392C32.0521 15.2215 30.9277 17.8702 28.6735 19.7799C26.4193 21.6896 23.3011 22.6403 19.3218 22.6403C18.6547 22.6403 17.7245 22.6099 16.5284 22.5409L16.1862 23.6121C20.3265 28.3289 24.3749 33.4413 28.3313 38.9633C31.577 43.5042 33.9692 46.7248 35.5631 48.6931L36.6451 50.5043L27.2272 63.7461C24.8119 67.1054 23.1861 69.3146 22.3581 70.3615C21.53 71.4084 20.8556 72.0628 20.3265 72.3273C19.7975 72.5919 18.7541 72.7351 17.213 72.7581L16.9369 73.0342V74.6908L17.2461 75C18.6952 74.908 19.8609 74.8675 20.735 74.8675C21.5861 74.8675 22.762 74.908 24.2571 75C28.7425 67.4763 32.6372 61.4324 35.9495 56.8768L38.4669 53.3979L48.9558 68.8927C50.1289 70.9174 51.4069 72.9523 52.7871 75C55.1562 74.908 56.9246 74.8675 58.0978 74.8675C58.9948 74.8675 60.5387 74.908 62.724 75L63 74.7239V73.0342L62.724 72.7581C61.2518 72.7351 60.3115 72.6324 59.8975 72.4599C59.4834 72.2873 58.9309 71.7664 58.2524 70.8806C57.5738 69.9948 56.6845 68.6552 55.5804 66.8605L42.6735 48.2293C42.6472 48.1034 42.599 47.9781 42.541 47.8427L45.0584 44.4964L51.4732 35.7937C53.7734 32.6876 55.2966 30.7448 56.0442 29.9625C56.7917 29.1802 57.4054 28.6892 57.877 28.4936C58.3485 28.2981 59.3095 28.1651 60.7587 28.096L61.0347 27.8199V26.2737L60.7587 25.9535C58.8035 26.0225 57.7297 26.064 57.5457 26.0639C57.1317 26.064 55.9567 26.0225 54.0016 25.9535L40.8186 45.3026C40.3022 44.6413 39.5695 43.5772 38.4338 42.0888C36.6166 39.7074 34.9199 37.4476 33.3328 35.3078L28.4306 28.8249C26.7514 26.6161 25.5857 25.0617 24.9416 24.1643C28.4149 23.4971 31.1448 22.4944 33.123 21.1714C35.1012 19.8484 36.5935 18.2641 37.6057 16.4004C38.6177 14.5367 39.1293 12.6344 39.1293 10.7017C39.1293 7.80265 38.097 5.2975 36.0268 3.18068C33.9566 1.06396 30.0104 4.59432e-05 24.1909 0Z'
                  fill='black'
                />
              </Svg>
              <View style={{ marginTop: '5pt' }}>
                <LabelValueText label='Qty ' value={externalRxData.quantity} />
                <LabelValueText label=' ' value={externalRxData.productDescription} />
              </View>
            </View>
            <LabelValueText
              label='MED. NOTE: '
              value={externalRxData.notes}
              style={{
                marginBottom: '15pt',
              }}
            />
            <LabelValueText label='Instructions: ' value={externalRxData.directions} />
          </Col>
          <Col
            style={{
              width: '35%',
            }}
          >
            <LabelValueItem label='Phone' value={getFormattedPhone(patient?.phoneNumber)} valueWidth='132pt' />
            <LabelValueItem
              label='DOB'
              value={patient?.dateOfBirth && dateMDY(patient.dateOfBirth)}
              valueWidth='132pt'
            />
            <Row
              style={{
                justifyContent: 'flex-start',
              }}
            >
              <StyledText
                content='Gender'
                style={{
                  marginRight: '16pt',
                }}
              />
              <StyledText content={patient?.gender?.toUpperCase()} weight={600} />
            </Row>
          </Col>
        </Row>
        <Row>
          <Col
            style={{
              width: '60%',
            }}
          >
            <Row
              style={{
                justifyContent: 'flex-start',
                alignItems: 'center',
              }}
            >
              <StyledText content='D A W' style={{ marginRight: '48pt' }} />
              <Svg
                width='12'
                height='9'
                viewBox='0 0 12 9'
                fill='none'
                style={{
                  marginRight: '115pt',
                }}
              >
                <Path
                  d='M0.792 9V0.431999H2.724L5.688 6.528H5.796C5.78 6.328 5.76 6.092 5.736 5.82C5.712 5.54 5.692 5.256 5.676 4.968C5.66 4.672 5.652 4.404 5.652 4.164V0.431999H7.212V9H5.292L2.304 2.868H2.22C2.244 3.084 2.264 3.344 2.28 3.648C2.304 3.944 2.324 4.24 2.34 4.536C2.356 4.832 2.364 5.092 2.364 5.316V9H0.792ZM8.028 9V7.896H11.772V9H8.028ZM9.888 7.392C9.504 7.392 9.164 7.308 8.868 7.14C8.572 6.972 8.336 6.732 8.16 6.42C7.992 6.1 7.908 5.712 7.908 5.256C7.908 4.576 8.088 4.052 8.448 3.684C8.808 3.316 9.296 3.132 9.912 3.132C10.288 3.132 10.624 3.216 10.92 3.384C11.224 3.544 11.46 3.784 11.628 4.104C11.804 4.416 11.892 4.8 11.892 5.256C11.892 5.936 11.712 6.464 11.352 6.84C10.992 7.208 10.504 7.392 9.888 7.392ZM9.9 6.432C10.18 6.432 10.38 6.336 10.5 6.144C10.628 5.944 10.692 5.648 10.692 5.256C10.692 4.864 10.628 4.572 10.5 4.38C10.38 4.188 10.18 4.092 9.9 4.092C9.62 4.092 9.416 4.188 9.288 4.38C9.16 4.572 9.096 4.864 9.096 5.256C9.096 5.648 9.16 5.944 9.288 6.144C9.416 6.336 9.62 6.432 9.9 6.432Z'
                  fill='#323942'
                />
              </Svg>
            </Row>
          </Col>
          <Col
            style={{
              width: '35%',
            }}
          >
            <LabelValueItem label='Refills' value={externalRxData.totalRefills} valueWidth='132pt' />
          </Col>
        </Row>
        <View
          style={{
            width: '100%',
            height: '4pt',
            backgroundColor: 'black',
          }}
        />
      </View>
    </PageContainer>
  );
};
